import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const ThemeCtx = createContext({
  theme: 'dark',
  setTheme: () => {},
  fontSize: 15,
  setFontSize: () => {},
});

// Font preference still persists (it's a per-user comfort knob, nothing
// to do with time of day). The theme key was dropped — see below.
const FONT_KEY = 'arthum.fontSize.v1';

// Time-of-day theme boundaries — in IST (Asia/Kolkata, UTC+05:30),
// NOT the user's local device clock. sutrumai.com is an India-centric
// product, so theme follows India's day/night even when the visitor
// is in a different timezone (a user in London at 1 AM IST sees the
// same dark mode an IST user would).
//
// 6 AM IST ≤ hour < 6 PM IST → light. Otherwise → dark.
const DAY_START_H_IST = 6;
const DAY_END_H_IST = 18;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function timeBasedTheme() {
  // Wrap in try/catch — `new Date()` is fine but server-rendered
  // contexts (which we don't have today) might null it out.
  try {
    // Shift epoch to IST, then read UTC hours (those now represent IST
    // hours). Avoids `toLocaleString({ timeZone })` overhead and works
    // identically across all browsers / device timezones.
    const istHour = new Date(Date.now() + IST_OFFSET_MS).getUTCHours();
    return istHour >= DAY_START_H_IST && istHour < DAY_END_H_IST ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function ThemeProvider({ children }) {
  // Initial value is derived from local clock — no localStorage hydration
  // for theme. Every fresh visit re-syncs to whatever time it is.
  const [theme, setThemeState] = useState(timeBasedTheme);
  const [fontSize, setFontSizeState] = useState(() => {
    try {
      const v = Number(localStorage.getItem(FONT_KEY));
      return Number.isFinite(v) && v >= 12 && v <= 20 ? v : 15;
    } catch { return 15; }
  });

  // Manual override book-keeping. When the user clicks Light/Dark we
  // capture the natural theme AT THAT MOMENT. The next time the natural
  // theme is different (i.e. the clock crossed 6AM or 6PM), the
  // override is dropped — auto resumes for the new period. This gives
  // a manual "stick for the rest of this period" gesture without ever
  // permanently freezing the user away from auto behaviour.
  const overrideRef = useRef(null);  // { theme, naturalAtOverride } | null

  // Mirror theme to <body class> so the CSS variables flip globally.
  useEffect(() => {
    document.body.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.setProperty('--fs', `${fontSize}px`);
    try { localStorage.setItem(FONT_KEY, String(fontSize)); } catch {}
  }, [fontSize]);

  // ── Auto-sync loop ──
  // Re-check the clock every minute and on window focus. If the natural
  // theme has changed since we last looked, drop any manual override
  // and adopt the new natural theme. If there's no override, follow
  // natural directly.
  useEffect(() => {
    const tick = () => {
      const natural = timeBasedTheme();
      if (overrideRef.current && overrideRef.current.naturalAtOverride !== natural) {
        overrideRef.current = null;
      }
      if (!overrideRef.current) {
        setThemeState((prev) => (prev === natural ? prev : natural));
      }
    };
    // 60s is enough — the boundary is a hard hour edge and we only
    // care about being right within a minute of it.
    const id = setInterval(tick, 60 * 1000);
    const onFocus = () => tick();
    const onVisible = () => { if (!document.hidden) tick(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Manual toggle from the header. Records the natural theme at the
  // moment of the override so the auto-sync loop can decide when to
  // expire it (at the next natural boundary crossing).
  const setTheme = useCallback((t) => {
    const target = t === 'light' ? 'light' : 'dark';
    overrideRef.current = { theme: target, naturalAtOverride: timeBasedTheme() };
    setThemeState(target);
  }, []);

  const setFontSize = useCallback((n) => setFontSizeState(Math.min(20, Math.max(12, n))), []);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme, fontSize, setFontSize }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
